"use client";

import React, { createContext, useState, useEffect, ReactNode, useCallback } from "react";
import { jwtDecode } from "jwt-decode";

export interface User {
  token: string;
  id: string;
  email: string;
  name: string;
  profileImage?: string;
  role: string; // "admin" or "worker"
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (token: string, remember: boolean) => void;
  logout: () => void;
  isTokenValid: (token: string) => boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  login: () => {},
  logout: () => {},
  isTokenValid: () => false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isTokenValid = useCallback((token: string): boolean => {
    try {
      const decoded: any = jwtDecode(token);
      return decoded.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }, []);

  const login = useCallback((token: string, remember: boolean) => {
    try {
      if (!isTokenValid(token)) {
        throw new Error("Invalid or expired token");
      }

      const storage = remember ? localStorage : sessionStorage;
      storage.setItem("token", token);

      const decoded = jwtDecode<Omit<User, "token">>(token);
      const userData = { token, ...decoded };
      setUser(userData);

    } catch (error) {
      console.error("Login error:", error);
      logout();
      throw error;
    }
  }, [isTokenValid]);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    setUser(null);
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        
        if (token && isTokenValid(token)) {
          const decoded = jwtDecode<Omit<User, "token">>(token);
          setUser({ token, ...decoded });
        } else if (token) {
          // Token exists but is invalid
          logout();
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isTokenValid, logout]);

  // Auto logout when token expires
  useEffect(() => {
    if (!user?.token) return;

    const decoded: any = jwtDecode(user.token);
    const timeUntilExpiry = decoded.exp * 1000 - Date.now();
    
    if (timeUntilExpiry <= 0) {
      logout();
      return;
    }

    const logoutTimer = setTimeout(logout, timeUntilExpiry);
    return () => clearTimeout(logoutTimer);
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, isTokenValid }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;